import React, { useMemo } from 'react';
import { BarChart, PieChart, DataTable, Tooltip, Icon, CodeDisplay, Button, EmptyState } from '@aidkitorg/component-library';
import type { ColumnDef } from '@tanstack/react-table';
import type * as survey from '@aidkitorg/types/lib/survey';
import { renderLinkCell } from '../../Reports/utils/reportColumns';
import type { LinkTemplateGenerator } from '../../../utils/dashboardUtils';
import { toAdminPanelHref } from '../../../utils/dashboardUtils';
import { useLocalizedStrings } from '../../../Localization';
import { createValueFormatter } from '../../../DistroDashboard/ChartTools';

/**
 * Render a Custom Number (metric) visualization
 */
export const CustomNumberChart: React.FC<{
  results: any; // Can be a single value or an array
  visualization: survey.CustomNumber;
}> = ({ results, visualization }) => {
  // Custom Number results can be:
  // 1. A single value (number/string) - from backend Custom Number handling
  // 2. An array with first row/column - legacy format
  const value = useMemo(() => {
    if (results === undefined || results === null) return '--';

    // If it's not an array, it's a direct value
    if (!Array.isArray(results)) {
      return results;
    }

    // Array format - extract first row/column
    if (results.length === 0) return '--';
    const firstRow = results[0];
    const keys = Object.keys(firstRow);
    if (keys.length === 0) return '--';
    return firstRow[keys[0]];
  }, [results]);

  const prefix = visualization.prefix || '';
  const suffix = visualization.suffix || '';
  const decimalPlaces = visualization.decimalPlaces ?? 0;

  const formattedValue = useMemo(() => {
    if (value === '--' || value === null || value === undefined) return '--';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return String(value);

    if (visualization.useCommas) {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: decimalPlaces,
        minimumFractionDigits: decimalPlaces,
      }).format(numValue);
    }
    return numValue.toFixed(decimalPlaces);
  }, [value, visualization.useCommas, decimalPlaces]);

  return (
    <div className="text-[clamp(1.25rem,2.5vw,1.75rem)] font-semibold text-center leading-tight">
      {prefix}{formattedValue}{suffix}
    </div>
  );
};

/**
 * Render a Custom Bar Chart visualization
 */
export const CustomBarChartRenderer: React.FC<{
  results: any[];
  visualization: survey.CustomBarChart;
  height: number;
  title: string;
}> = ({ results, visualization, height, title }) => {
  const { processedData, categoryKey, valueKey } = useMemo(() => {
    if (!results || results.length === 0) {
      return { processedData: [], categoryKey: '', valueKey: '' as string | string[] };
    }

    const { groupKeys, summaryKey } = visualization;

    // Convert groupKey values to numbers for proper Y-axis scaling
    const processedData = results.map((item: Record<string, any>) => {
      const processed = { ...item };
      for (const key of groupKeys) {
        processed[key] = Number(item[key]) || 0;
      }
      return processed;
    });

    return {
      processedData,
      categoryKey: summaryKey,
      valueKey: groupKeys.length === 1 ? groupKeys[0] : groupKeys,
    };
  }, [results, visualization]);

  const numberFormat = visualization.numberFormat;
  const valueFormatter = useMemo(() => createValueFormatter(numberFormat), [numberFormat]);

  if (processedData.length === 0) {
    return (
      <EmptyState variant="widget" />
    );
  }

  return (
    <BarChart
      data={processedData}
      categoryKey={categoryKey}
      valueKey={valueKey}
      height={height}
      tableCaption={title}
      stack={visualization.stack}
      suffix={numberFormat ? undefined : visualization.units}
      valueFormatter={valueFormatter}
      showBarLabels={visualization.showBarLabels === true}
    />
  );
};

/**
 * Render a Custom Pie Chart visualization
 */
export const CustomPieChartRenderer: React.FC<{
  results: any[];
  visualization: survey.CustomPieChart;
  height: number;
  title: string;
}> = ({ results, visualization, height, title }) => {
  const { processedData, nameKey, dataKey } = useMemo(() => {
    if (!results || results.length === 0) {
      return { processedData: [], nameKey: '', dataKey: '' };
    }

    const { groupKeys, summaryKey } = visualization;
    const primaryGroupKey = groupKeys[0];

    // Convert string values to numbers
    const processedData = results.map((item: Record<string, any>) => ({
      ...item,
      [summaryKey]: Number(item[summaryKey]) || 0,
    }));

    return {
      processedData,
      nameKey: primaryGroupKey,
      dataKey: summaryKey,
    };
  }, [results, visualization]);

  if (processedData.length === 0) {
    return (
      <EmptyState variant="widget" />
    );
  }

  return (
    <PieChart
      data={processedData}
      nameKey={nameKey}
      dataKey={dataKey}
      height={height}
      tableCaption={title}
    />
  );
};

/**
 * Render a Custom Query Table visualization
 */
export const CustomTableRenderer: React.FC<{
  results: any[];
  visualization: survey.Table;
  height: number;
  // LinkTo generators so LinkTo links can render correctly
  linkGensByCol?: Record<string, LinkTemplateGenerator[]>;
  // Column header names known from static config (e.g. ApplicantTable.columns).
  // Used to render headers when results is empty so the table doesn't collapse.
  staticColumnHeaders?: string[];
}> = ({ results, visualization, height, linkGensByCol, staticColumnHeaders }) => {
  const L = useLocalizedStrings();
  // Check if we should show the view button
  const showViewButton = !visualization.hideViewButton;
  const hasUidColumn = results?.length > 0
    ? 'uid' in results[0]
    : !!staticColumnHeaders?.includes('uid');

  // Get the link path and label from visualization config
  const linkSubsurvey = visualization.linksToSection?.subsurvey;
  const linkLabel = useMemo(() => {
    const label = visualization.linksToSection?.label;
    if (!label) return 'View';
    if (typeof label === 'string') return label;
    return label.en || 'View';
  }, [visualization.linksToSection?.label]);

  // Auto-generate columns from the first row's keys
  const columns = useMemo((): ColumnDef<any>[] => {
    const isEmpty = !results || results.length === 0;
    if (isEmpty && (!staticColumnHeaders || staticColumnHeaders.length === 0)) {
      return [];
    }

    // When empty, use the statically-known headers so the table still renders
    // its header row (matching standalone DataTable behavior).
    const keys = isEmpty
      ? staticColumnHeaders!
      : Object.keys(results[0]);

    const dataColumns: ColumnDef<any>[] = keys.map((key) => {
      const colGens = linkGensByCol?.[key];
      const isLinkColumn = !!colGens?.length;
      const headerGen = colGens?.[0];

      return {
        accessorKey: key,
        // Hide header if any LinkTo for this column requested it
        header: headerGen?.hideHeader ? '' : key,
        enableSorting: !isLinkColumn && key !== 'uid',
        cell: ({ getValue, row }: any) => {
          const value = getValue();
          if (value === null || value === undefined) return '--';
          if (typeof value === 'object') return JSON.stringify(value);
          if (key === 'uid') return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />;

          const stringValue = String(value);

          // LinkTo column: pick the matching generator by regex (mirrors ReportViewPage).
          if (isLinkColumn) {
            const matched = stringValue
              ? colGens!.find((g) => g.templateRegex.test(stringValue))
              : undefined;
            if (matched) {
              return (
                <Button href={toAdminPanelHref(stringValue, matched.linkTo)} variant="link" openInNewWindow className="!justify-start">
                  {matched.label || key || L.admin_panel.reports.view_url}
                </Button>
              );
            }
          }

          // Fallback: detect absolute http/https URLs (matches ReportViewPage Custom Query handling).
          try {
            const url = new URL(stringValue);
            if (url.protocol === 'http:' || url.protocol === 'https:') {
              return renderLinkCell(stringValue, 'view');
            }
          } catch {
            // Not a URL, fall through to string rendering
          }

          return stringValue;
        },
      };
    });

    // Add view button column if enabled and uid column exists
    if (showViewButton && hasUidColumn) {
      const viewColumn: ColumnDef<any> = {
        id: 'actions',
        header: linkLabel,
        cell: ({ row }) => {
          const uid = row.original.uid;
          if (!uid) return null;

          const href = linkSubsurvey ? `/admin-panel/a/${uid}/${linkSubsurvey.path}` : `/admin-panel/a/${uid}`;

          return (
            <Tooltip text={linkLabel}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex text-gray-700"
                aria-label={linkLabel}
              >
                <Icon name="EyeIcon" type="solid" size="md" role="decorative" />
              </a>
            </Tooltip>
          );
        },
      };
      dataColumns.push(viewColumn);
    }

    return dataColumns;
  }, [results, showViewButton, hasUidColumn, linkSubsurvey, linkLabel, linkGensByCol, L, staticColumnHeaders]);

  if ((!results || results.length === 0) && columns.length === 0) {
    return (
      <EmptyState variant="widget" />
    );
  }

  return (
    <DataTable
      className="p-2 w-full"
      columns={columns}
      data={results}
      copyColumnValues={hasUidColumn && !visualization.hideUidCopyButton ? { columnIds: ['uid'], label: 'Copy UIDs' } : undefined}
      pageSize={10}
      compact
      stickyHeader={true}
      tableHeight={height}
      alternateRowShading
      enableSearch
      searchLabel={L.admin_panel.search_placeholder}
      noDataMessage={<EmptyState variant="widget" />}
    />
  );
};

/**
 * Empty state component for when no data is available
 */
export const NoDataPlaceholder: React.FC = () => <EmptyState variant="widget" />;
